import type { BoardUpdateFields, CanonicalSearchLoad } from '@numeo/types'
import { EmailAgentMode, SearchAgentOption } from '@numeo/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AgentRequest, AutonomousRun, RateHistoryEntry, RowUiState } from '../types/domain'
import type {
    AgenticMessage,
    AgenticMessageType,
    AgenticSurface,
    LaneRuntimeState,
    QueuedInput,
    StreamingEvent,
    TaskChildEvent,
    ToolCallState,
} from '../types/messaging'
import type { SearchAgentParams, SearchFormValues } from '../types/search'
import type { AgenticHubState, ChatQuestionDraft, LocalAnswer, MessagePaginationState, ResponseFilter, TabInfo, TabPhase, TabState } from '../types/state'
import { tabUnreadCount } from '../utils/tab-counts'
import { countsLiveBoard } from './live-board'

export type {
    AgentRequest,
    AgenticHubState,
    AgenticMessage,
    AgenticMessageType,
    AgenticSurface,
    AutonomousRun,
    LocalAnswer,
    RateHistoryEntry,
    ResponseFilter,
    SearchFormValues,
    TabPhase,
    TabState,
    ToolCallState,
}

const initialState: AgenticHubState = {
    tabs: {},
    activeTabId: null,
    tabOrder: [],
    deletingTabIds: [],
    hubPageMounted: false,
    aiHubSearchUnlimited: false,
    searchFlowExperimentsV2Enabled: false,
    aiHubLoadsUiExperimentsEnabled: false,
    deepagentChatPlanningPromptEnabled: false,
    autonomousMaxRunMs: null,
    pillCollapseRequestNonce: 0,
    pillExpandRequestNonce: 0,
    scoringSummaryFlash: null,
    activeEmailLoadId: null,
    activeEmailTabId: null,
    activeBrokerInquiry: null,
    unreadByEmail: {},
    routedMiles: {},
    inquiryEmailVersions: {},
}

/** Recompute unreadByEmail as the sum of each tab's unread count (live once loaded, else the startup
 *  seed — see tabUnreadCount), grouped by sender_email. */
/**
 * Get-or-create this row's client-side UI state.
 *
 * Lives on the tab, keyed by `rowNumber`, rather than on the load — the server never sends these, so a
 * field on the row would be wiped by every refetch and had to be snapshotted and pasted back. Beside
 * the rows there is nothing to preserve.
 */
function rowUi(tab: TabState, rowNumber: number): RowUiState {
    if (!tab._rowUi) tab._rowUi = {}
    if (!tab._rowUi[rowNumber]) tab._rowUi[rowNumber] = {}
    return tab._rowUi[rowNumber]
}

function recalcUnreadByEmail(state: AgenticHubState): void {
    const byEmail: Record<string, number> = {}
    for (const tabState of Object.values(state.tabs)) {
        const email = tabState.tab.sender_email
        if (!email) continue
        byEmail[email] = (byEmail[email] ?? 0) + tabUnreadCount(tabState)
    }
    state.unreadByEmail = byEmail
}

function createTabState(tabInfo: TabInfo): TabState {
    if (!tabInfo.phase) tabInfo = { ...tabInfo, phase: 'idle' }
    const createLaneRuntime = (): LaneRuntimeState => ({
        streamingText: '',
        streamingThinking: '',
        streamingMessageId: null,
        isSending: false,
        isStreaming: false,
        activeToolCalls: {},
        streamingEventLog: [],
        sendFailure: null,
    })
    const createMessagePagination = (): MessagePaginationState => ({
        page: 1,
        hasMore: true,
        loading: false,
    })

    return {
        tab: tabInfo,
        messages: [],
        streamingText: '',
        streamingThinking: '',
        streamingMessageId: null,
        isSending: false,
        isStreaming: false,
        activeToolCalls: {},
        streamingEventLog: [],
        chatRuntime: createLaneRuntime(),
        searchRuntime: createLaneRuntime(),
        loads: [],
        totalLoadsCount: null,
        loadsLoading: true,
        loadsSearchQuery: '',
        statusFilter: [],
        responseFilter: 'all',
        queuedInputs: [],
        pendingRequests: [],
        searchBarCollapsed: false,
        messagePagination: {
            chat: createMessagePagination(),
            search: createMessagePagination(),
        },
        hasMoreMessages: true,
        messagesPage: 1,
        loadingMoreMessages: false,
        searchForm: null,
        searchFormSyncNonce: 0,
        clearedFormKeys: [],
        searchFormReplaceNonce: 0,
        hasSearched: false,
        firstSearchLatencyMs: null,
        processingSearch: null,
        searchParamsArrived: false,
        analyzingBoard: null,
        scoringStartedAt: null,
        scoringProgress: null,
        analyzingAnswers: false,
        liveChatQuestionCorrelationIds: [],
        chatQuestionDrafts: {},
        loadStreamingStates: {},
        threadStreamingStates: {},
        autonomousRuns: [],
        autonomousStartedAt: null,
        autonomousExpired: false,
        unreadCount: 0,
        seedPending: 0,
        requestsFetched: false,
    }
}

function getLaneRuntime(tab: TabState, lane: AgenticSurface = 'chat'): LaneRuntimeState {
    return lane === 'search' ? tab.searchRuntime : tab.chatRuntime
}

function syncChatRuntimeAliases(tab: TabState): void {
    tab.streamingText = tab.chatRuntime.streamingText
    tab.streamingThinking = tab.chatRuntime.streamingThinking
    tab.streamingMessageId = tab.chatRuntime.streamingMessageId
    tab.isSending = tab.chatRuntime.isSending
    tab.isStreaming = tab.chatRuntime.isStreaming
    tab.activeToolCalls = tab.chatRuntime.activeToolCalls
    tab.streamingEventLog = tab.chatRuntime.streamingEventLog
}

function syncChatMessagePaginationAliases(tab: TabState): void {
    tab.hasMoreMessages = tab.messagePagination.chat.hasMore
    tab.messagesPage = tab.messagePagination.chat.page
    tab.loadingMoreMessages = tab.messagePagination.chat.loading
}

function getMessagePagination(tab: TabState, surface: AgenticSurface = 'chat'): MessagePaginationState {
    return tab.messagePagination[surface]
}

const MAX_EVENT_LOG_SIZE = 500
const EVENT_LOG_TRIM_TO = 400

function capEventLog(runtime: LaneRuntimeState): void {
    if (runtime.streamingEventLog.length > MAX_EVENT_LOG_SIZE) {
        runtime.streamingEventLog = runtime.streamingEventLog.slice(-EVENT_LOG_TRIM_TO)
    }
}

/** Mark the most recent streaming thinking event in a task's child-event list as no-longer-streaming.
 *  Called when a tool start or reply chunk arrives so the cursor blink stops on the prior thinking
 *  block. Mirrors `settleThinking` from the Pane A mock. */
function settleStreamingThinking(events: TaskChildEvent[]): void {
    for (let i = events.length - 1; i >= 0; i--) {
        const ev = events[i]
        if (ev.kind === 'thinking') {
            ev.streaming = false
            return
        }
    }
}

/** Append a thinking chunk to a task's child events. If the trailing event is a still-streaming
 *  thinking block, merge into it (so consecutive AH_Thinking deltas concatenate into one paragraph
 *  rather than rendering as N tiny blocks). Otherwise settle any prior thinking and push a new
 *  streaming entry. Mirrors `pushThinking` from the Pane A mock. */
function pushStreamingThinking(events: TaskChildEvent[], text: string): void {
    const last = events[events.length - 1]
    if (last && last.kind === 'thinking' && last.streaming) {
        last.content = last.content ? last.content + text : text
        return
    }
    settleStreamingThinking(events)
    events.push({ kind: 'thinking', content: text, streaming: true })
}

/** Append a reply chunk to a task's child events. Reply chunks always merge into the trailing
 *  reply (the model streams token-by-token; we don't want N tiny bubbles). Settles any prior
 *  thinking before opening a new reply. Mirrors `pushReply` from the Pane A mock. */
function pushStreamingReply(events: TaskChildEvent[], text: string): void {
    const last = events[events.length - 1]
    if (last && last.kind === 'reply') {
        last.content += text
        return
    }
    settleStreamingThinking(events)
    events.push({ kind: 'reply', content: text })
}

function sortMessagesChronologically(messages: AgenticMessage[]): AgenticMessage[] {
    return [...messages].sort((a, b) => {
        const left = a.created_at ? new Date(a.created_at).getTime() : 0
        const right = b.created_at ? new Date(b.created_at).getTime() : 0
        return left - right
    })
}

/** Stable cross-source dedup key — reconciles an optimistic row (timestamp/`${tcId}-call` id)
 *  with its DB copy (uuid id) that arrives later via the API. Research rows key by
 *  `tool_call_id`: their content is empty (NULL in PG → `''` after normalize), so a content
 *  key collapses to `agent-tool-call::` and drops EVERY tool call after the first (it shows
 *  live, then vanishes on finalize until a refresh re-fetches by id). Other rows key by content
 *  when non-empty (still catches the same agent-response text from optimistic + watchdog fetch).
 *  Returns null when there's no stable key → caller dedups by id only. */
function messageDedupKey(m: AgenticMessage): string | null {
    const surface = m.surface || 'chat'
    if (m.tool_call_id) return `${surface}::${m.message_type}::tc::${m.tool_call_id}`
    if (m.content) return `${surface}::${m.message_type}::${m.content}`
    return null
}

function replaceMessagesForSurface(tab: TabState, surface: AgenticSurface, messages: AgenticMessage[]): void {
    const normalized = messages.map((message) => ({ ...message, surface: message.surface || surface }))
    const otherMessages = tab.messages.filter((message) => (message.surface || 'chat') !== surface)
    tab.messages = sortMessagesChronologically([...otherMessages, ...normalized])
}

const agenticHubSlice = createSlice({
    name: 'agenticHub',
    initialState,
    reducers: {
        addTab(
            state,
            action: PayloadAction<{
                id: string
                title: string
                model_name?: string | null
                status: string
                mode?: EmailAgentMode
                started?: boolean
                sender_email?: string | null
                phase?: TabPhase
                /** Optional pre-seeded search form. Set when a tab is spawned from an edit
                 *  on an already-searched tab so AgenticSearchForm's useState initializer
                 *  reads the right form on its very first mount. Without this, the new tab
                 *  is briefly born with searchForm=null and the form inputs initialize
                 *  empty before a follow-up setSearchForm dispatch can land. */
                searchForm?: SearchFormValues | null
                /** BE-stamped autonomous run start for FullyAutonomous tabs.
                 *  Carried in from /tabs so the FE cutoff tick has a starting point
                 *  on first mount (otherwise it has to wait for the next event). */
                autonomousStartedAt?: string | null
                /** Server-stamped creation time, carried through so the migration notice can tell
                 *  which open tabs predate the unified-load-model release. */
                created_at?: string | null
            }>
        ) {
            const { id, title, model_name, status, mode, started, sender_email, phase, searchForm, autonomousStartedAt, created_at } = action.payload
            const tabState = createTabState({ id, title, model_name, status, mode, started, sender_email, phase: phase ?? 'idle', created_at })
            if (searchForm !== undefined) tabState.searchForm = searchForm
            if (autonomousStartedAt !== undefined) tabState.autonomousStartedAt = autonomousStartedAt
            state.tabs[id] = tabState
            if (!state.tabOrder.includes(id)) {
                state.tabOrder.push(id)
            }
        },

        setHubPageMounted(state, action: PayloadAction<boolean>) {
            state.hubPageMounted = action.payload
        },

        setActiveTab(state, action: PayloadAction<string | null>) {
            // Coerce to null if the payload references a tab that no longer exists.
            // Guards against stale ids from URL hash, prior session memory, or race conditions during deletion.
            const nextId = action.payload && state.tabs[action.payload] ? action.payload : null
            state.activeTabId = nextId
            // Clear email panel if switching away from the tab that owns the active email
            if (state.activeEmailTabId && state.activeEmailTabId !== nextId) {
                state.activeEmailLoadId = null
                state.activeEmailTabId = null
            }
            // Same for broker inquiry — thread belongs to a specific tab.
            if (state.activeBrokerInquiry && state.activeBrokerInquiry.tabId !== nextId) {
                state.activeBrokerInquiry = null
            }
        },

        setActiveEmailLoad(state, action: PayloadAction<{ tabId: string; loadId: string } | null>) {
            if (action.payload) {
                state.activeEmailLoadId = action.payload.loadId
                state.activeEmailTabId = action.payload.tabId
                // Opening a load email replaces any broker-inquiry thread — only one panel view at a time.
                state.activeBrokerInquiry = null
            } else {
                state.activeEmailLoadId = null
                state.activeEmailTabId = null
            }
        },

        clearActiveEmailLoad(state) {
            state.activeEmailLoadId = null
            state.activeEmailTabId = null
        },

        setActiveBrokerInquiry(
            state,
            action: PayloadAction<{ tabId: string; threadId: string; brokerName?: string | null; brokerEmail?: string | null } | null>
        ) {
            if (action.payload) {
                state.activeBrokerInquiry = action.payload
                // Opening a broker inquiry replaces any load email — only one panel view at a time.
                state.activeEmailLoadId = null
                state.activeEmailTabId = null
            } else {
                state.activeBrokerInquiry = null
            }
        },

        clearActiveBrokerInquiry(state) {
            state.activeBrokerInquiry = null
        },

        removeTab(state, action: PayloadAction<string>) {
            const tabId = action.payload
            delete state.tabs[tabId]
            state.tabOrder = state.tabOrder.filter((id) => id !== tabId)
            state.deletingTabIds = state.deletingTabIds.filter((id) => id !== tabId)
            if (state.activeTabId === tabId) {
                state.activeTabId = state.tabOrder[0] || null
            }
            // Clear email panel if removing the tab that owns it
            if (state.activeEmailTabId === tabId) {
                state.activeEmailLoadId = null
                state.activeEmailTabId = null
            }
        },

        clearAllTabs(state) {
            state.tabs = {}
            state.tabOrder = []
            state.activeTabId = null
            state.deletingTabIds = []
        },

        setTabStatus(state, action: PayloadAction<{ tabId: string; status: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.tab.status = action.payload.status
        },

        /**
         * Update the replay cursor for a tab. Called after any buffered AgenticHub event is
         * processed. The ID is used on reconnect to request missed events via /v1/replay.
         * Monotonic — later IDs sort lexicographically after earlier ones.
         */
        setLastEventId(state, action: PayloadAction<{ tabId: string; eventId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const existing = tab.lastEventId
            // Only advance; never regress (guards against out-of-order socket + replay delivery).
            if (!existing || action.payload.eventId > existing) {
                tab.lastEventId = action.payload.eventId
            }
        },

        setTabMode(state, action: PayloadAction<{ tabId: string; mode: EmailAgentMode }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.tab.mode = action.payload.mode
        },

        /** Repoints which connected email a tab sends NEW mail from. Threads it already opened keep
         *  the address they were opened with — the server derives that from the thread itself, so
         *  nothing here needs to model it. Drives the tab's chip and the top-bar picker. */
        setTabSenderEmail(state, action: PayloadAction<{ tabId: string; senderEmail: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.tab.sender_email = action.payload.senderEmail
        },

        /** Sets the per-tab Agentic/Classic UI mode. Switching tabs preserves each tab's last-used mode.
         * Also writes the value to localStorage as the global default — new tabs and the next page
         * load will adopt this preference (see LAST_UI_MODE_KEY resolution in HubShell). */
        setTabUiMode(state, action: PayloadAction<{ tabId: string; uiMode: 'agentic' | 'classic' }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                tab.tab.uiMode = action.payload.uiMode
            }
        },

        setTabModelName(state, action: PayloadAction<{ tabId: string; modelName: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.tab.model_name = action.payload.modelName
        },

        setAiHubSearchUnlimited(state, action: PayloadAction<boolean>) {
            state.aiHubSearchUnlimited = action.payload
        },
        setSearchFlowExperimentsV2Enabled(state, action: PayloadAction<boolean>) {
            state.searchFlowExperimentsV2Enabled = action.payload
        },
        setAiHubLoadsUiExperimentsEnabled(state, action: PayloadAction<boolean>) {
            state.aiHubLoadsUiExperimentsEnabled = action.payload
        },
        setDeepagentChatPlanningPromptEnabled(state, action: PayloadAction<boolean>) {
            state.deepagentChatPlanningPromptEnabled = action.payload
        },
        setAutonomousMaxRunMs(state, action: PayloadAction<number | null>) {
            state.autonomousMaxRunMs = action.payload
        },
        /** Bumped each time something wants the ChatPill collapsed (e.g.
         *  `ranking_started` socket under v2 — the scoring overlay needs the
         *  table visible). ChatPill watches the value and collapses on change.
         *  Counter (not bool) so consecutive requests still fire even if no
         *  one cleared the prior one. */
        requestPillCollapse(state) {
            state.pillCollapseRequestNonce += 1
        },
        /** Bumped when something wants the ChatPill opened (e.g. the
         *  "Edit · Ask Numeo AI" hint on the ranking-criteria bar). ChatPill
         *  watches the value and expands on change. Mirror of requestPillCollapse. */
        requestPillExpand(state) {
            state.pillExpandRequestNonce += 1
        },

        /** Stamp the latest scoring tier counts + bump the nonce so the collapsed
         *  chat pill replays its "N recommended, …" typewriter flash. */
        setScoringSummaryFlash(state, action: PayloadAction<{ recommended: number; matched: number; potential: number; deduplicated: number }>) {
            const prev = state.scoringSummaryFlash?.nonce ?? 0
            state.scoringSummaryFlash = { ...action.payload, nonce: prev + 1 }
        },

        /** Seed per-tab unread + pending counts from GET /tabs/counts so every tab's badge (and the
         *  account-switcher / browser-tab totals) is populated upfront, before any tab is opened.
         *  The seed is the fallback used until a tab's full data lands — see tabUnreadCount /
         *  tabPendingCount. Pending totals derive from these via selectPendingCountByEmail. */
        setTabCounts(state, action: PayloadAction<{ tabId: string; unreadCount: number; pendingCount: number }[]>) {
            for (const { tabId, unreadCount, pendingCount } of action.payload) {
                const tab = state.tabs[tabId]
                if (!tab) continue
                tab.unreadCount = unreadCount
                tab.seedPending = pendingCount
            }
            recalcUnreadByEmail(state)
        },

        startAutonomousRun(state, action: PayloadAction<{ tabId: string; run: AutonomousRun }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const existing = tab.autonomousRuns.findIndex((r) => r.runId === action.payload.run.runId)
            if (existing >= 0) {
                tab.autonomousRuns[existing] = action.payload.run
            } else {
                tab.autonomousRuns.push(action.payload.run)
            }
        },

        completeAutonomousRun(state, action: PayloadAction<{ tabId: string; runId: string; status: 'done' | 'error'; label: string; errorMessage?: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const run = tab.autonomousRuns.find((r) => r.runId === action.payload.runId)
            if (!run) return
            run.status = action.payload.status
            run.label = action.payload.label
            run.errorMessage = action.payload.errorMessage
            run.endedAt = Date.now()
        },

        setTabPhase(state, action: PayloadAction<{ tabId: string; phase: TabPhase }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const current = tab.tab.phase
            // Once in 'results', never regress to idle/chatting — only 'searching'→'results' is allowed forward
            if (current === 'results' && (action.payload.phase === 'idle' || action.payload.phase === 'chatting')) return
            tab.tab.phase = action.payload.phase
        },

        setSearchAgentParams(state, action: PayloadAction<{ tabId: string; params: SearchAgentParams }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            tab.searchAgentParams = action.payload.params
            tab.searchParamsArrived = true
        },

        clearSearchAgentParams(state, action: PayloadAction<{ tabId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.searchAgentParams = undefined
        },

        setSearchAgentOptions(
            state,
            action: PayloadAction<{ tabId: string; messageId: string; title?: string; body?: string; options: SearchAgentOption[]; multiSelect?: boolean }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                tab.searchAgentOptions = {
                    messageId: action.payload.messageId,
                    title: action.payload.title,
                    body: action.payload.body,
                    options: action.payload.options,
                    multiSelect: action.payload.multiSelect,
                }
            }
        },

        clearSearchAgentOptions(state, action: PayloadAction<{ tabId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.searchAgentOptions = undefined
        },

        setDeletingTab(state, action: PayloadAction<string>) {
            if (!state.deletingTabIds.includes(action.payload)) {
                state.deletingTabIds.push(action.payload)
            }
        },

        setStreamingText(
            state,
            action: PayloadAction<{
                tabId: string
                text: string
                lane?: AgenticSurface
                /** When set, the chunk came from a subagent (load-analyst). It must NOT land in
                 *  the parent's dispatcher-facing reply; instead we route it as a `reply` entry on
                 *  the parent task card's `events` list so the dispatcher sees the analyst's
                 *  answer/narration between its tool calls (especially during 30-60s LLM windows
                 *  where no nested tool call is firing). Requires `parentToolCallId` to know which
                 *  task card. */
                subagent?: string
                parentToolCallId?: string
                /** Server-side stream message id of this chunk. Keys the event-log text segment so
                 *  AH_StreamRetract can excise an abandoned attempt (repetition-loop trip → retry)
                 *  without touching neighboring segments. */
                messageId?: string
            }>
        ) {
            if (action.payload.subagent) {
                // Subagent text → append to the parent task card's `events` list (as a `reply`
                // entry, settled-thinking-aware) instead of polluting the dispatcher's main
                // reply. If there's no parent identifier (shouldn't happen post-pairing on the
                // runner) we drop the chunk — same suppression contract as before.
                if (!action.payload.parentToolCallId) return
                const tab = state.tabs[action.payload.tabId]
                if (!tab) return
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                const parent = runtime.activeToolCalls[action.payload.parentToolCallId]
                if (!parent) return
                if (!parent.events) parent.events = []
                pushStreamingReply(parent.events, action.payload.text)
                if (lane === 'chat') syncChatRuntimeAliases(tab)
                return
            }
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                runtime.streamingText += action.payload.text
                // Track in event log: append to last text event or start a new one. A messageId
                // flip also breaks the segment — a retried model attempt streams under a fresh id,
                // and fusing it with the aborted attempt's text would make retraction impossible.
                const last = runtime.streamingEventLog[runtime.streamingEventLog.length - 1]
                if (last && last.kind === 'text' && (!action.payload.messageId || !last.messageId || last.messageId === action.payload.messageId)) {
                    last.content += action.payload.text
                    if (!last.messageId) last.messageId = action.payload.messageId
                } else {
                    runtime.streamingEventLog.push({ kind: 'text', content: action.payload.text, messageId: action.payload.messageId })
                }
                capEventLog(runtime)
                if (lane === 'chat') syncChatRuntimeAliases(tab)
            }
        },

        /** AH_StreamRetract: the server abandoned a streamed message mid-turn (repetition-loop trip
         *  → retry). Its text was never persisted server-side, so drop exactly that message's text
         *  segments from the live pane — otherwise the degraded text renders next to the retried
         *  reply and would even be committed to `tab.messages` by finalizeStream's event-log replay. */
        retractStreamedMessage(state, action: PayloadAction<{ tabId: string; lane?: AgenticSurface; messageId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const lane = action.payload.lane || 'chat'
            const runtime = getLaneRuntime(tab, lane)
            runtime.streamingEventLog = runtime.streamingEventLog.filter((e) => e.kind !== 'text' || e.messageId !== action.payload.messageId)
            runtime.streamingText = runtime.streamingEventLog
                .filter((e): e is Extract<StreamingEvent, { kind: 'text' }> => e.kind === 'text')
                .map((e) => e.content)
                .join('')
            if (lane === 'chat') syncChatRuntimeAliases(tab)
        },

        setStreamingThinking(
            state,
            action: PayloadAction<{
                tabId: string
                text: string
                lane?: AgenticSurface
                /** Same routing rules as `setStreamingText`: when the chunk came from a subagent,
                 *  append/merge into a streaming `thinking` entry on the parent task card's
                 *  `events` list instead of the lane's main thinking pane. Keeps Gemini-3's
                 *  reasoning blocks visible (so the dispatcher isn't staring at silence during
                 *  30–60s LLM windows) without polluting the parent's thinking pane with internal
                 *  subagent state. */
                subagent?: string
                parentToolCallId?: string
            }>
        ) {
            if (action.payload.subagent) {
                if (!action.payload.parentToolCallId) return
                const tab = state.tabs[action.payload.tabId]
                if (!tab) return
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                const parent = runtime.activeToolCalls[action.payload.parentToolCallId]
                if (!parent) return
                if (!parent.events) parent.events = []
                // Append/merge into the trailing in-progress thinking entry (so multiple AH_Thinking
                // deltas concatenate into one paragraph). Cursor blink renders only on the trailing
                // streaming thinking entry.
                pushStreamingThinking(parent.events, action.payload.text)
                if (lane === 'chat') syncChatRuntimeAliases(tab)
                return
            }
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                runtime.streamingThinking += action.payload.text
                // Track in event log: append to last thinking event or start a new one
                const last = runtime.streamingEventLog[runtime.streamingEventLog.length - 1]
                if (last && last.kind === 'thinking') {
                    last.content += action.payload.text
                } else {
                    runtime.streamingEventLog.push({ kind: 'thinking', content: action.payload.text })
                }
                capEventLog(runtime)
                if (lane === 'chat') syncChatRuntimeAliases(tab)
            }
        },

        setIsSending(state, action: PayloadAction<{ tabId: string; isSending: boolean; lane?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                runtime.isSending = action.payload.isSending
                // A fresh send supersedes a shown "agent did not respond" row (retry resends
                // through this same path, so this is also what dismisses it on retry).
                if (action.payload.isSending) runtime.sendFailure = null
                // ...and starts a new search turn, whose params have not arrived yet.
                if (action.payload.isSending && lane === 'search') tab.searchParamsArrived = false
                if (lane === 'chat') syncChatRuntimeAliases(tab)
            }
        },

        /** Send-timeout verdict from useSendAckWatchdog: no socket response AND the refetch
         *  found nothing new. Stores the failed message text so the inline row can offer Retry. */
        setSendFailure(state, action: PayloadAction<{ tabId: string; lane: AgenticSurface; message: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            getLaneRuntime(tab, action.payload.lane).sendFailure = action.payload.message
            if (action.payload.lane === 'chat') syncChatRuntimeAliases(tab)
        },

        setIsStreaming(state, action: PayloadAction<{ tabId: string; isStreaming: boolean; messageId?: string; lane?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                runtime.isSending = false // AH_StreamStart arrived — no longer in sending-only state
                if (action.payload.isStreaming) runtime.sendFailure = null // the agent responded after all

                // Reset buffers on EVERY stream start (one per run). The old not-streaming→
                // streaming guard let a new run's StreamStart arriving while still flagged
                // streaming accumulate onto the prior run → replicated reply ("Waiting…Waiting…").
                runtime.isStreaming = action.payload.isStreaming
                if (action.payload.messageId !== undefined) {
                    runtime.streamingMessageId = action.payload.messageId
                }
                if (action.payload.isStreaming) {
                    runtime.streamingText = ''
                    runtime.streamingThinking = ''
                    runtime.streamingEventLog = []
                    // A new search run has no params yet — also covers a turn started from the queue, without our send.
                    if (lane === 'search') tab.searchParamsArrived = false
                }
                if (!action.payload.isStreaming) {
                    runtime.streamingMessageId = null
                    runtime.streamingText = ''
                    runtime.streamingThinking = ''
                    runtime.streamingEventLog = []
                }
                if (lane === 'chat') syncChatRuntimeAliases(tab)
            }
        },

        setMessages(state, action: PayloadAction<{ tabId: string; messages: AgenticMessage[]; surface?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            if (action.payload.surface) {
                replaceMessagesForSurface(tab, action.payload.surface, action.payload.messages)
                return
            }
            tab.messages = sortMessagesChronologically(action.payload.messages)
        },

        // Merge API-fetched messages with existing ones without wiping in-flight streaming state.
        // Deduplicates by both id and message_type::content (locally-added messages have timestamp IDs
        // while DB copies have UUIDs, so content-based dedup prevents duplicates after watchdog recovery).
        mergeMessages(state, action: PayloadAction<{ tabId: string; messages: AgenticMessage[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const existingIds = new Set(tab.messages.map((m) => m.id))
            const existingKeys = new Set(tab.messages.map(messageDedupKey).filter((k): k is string => k !== null))
            const newMessages = action.payload.messages.filter((m) => {
                if (existingIds.has(m.id)) return false
                const key = messageDedupKey(m)
                return !(key !== null && existingKeys.has(key))
            })
            if (newMessages.length > 0) {
                tab.messages = sortMessagesChronologically([...tab.messages, ...newMessages])
            }
        },

        addMessage(state, action: PayloadAction<{ tabId: string; message: AgenticMessage }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const surface =
                    action.payload.message.surface ||
                    (action.payload.message.message_type === 'user' || action.payload.message.message_type === 'agent-response' ? 'chat' : 'search')
                tab.messages.push({ ...action.payload.message, surface })
            }
        },

        upsertNewLoadsAlert(state, action: PayloadAction<{ tabId: string; content: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            // Only update the card if:
            // 1. It's the last message (nothing persisted after it)
            // 2. The agent isn't streaming (streaming means it's already processing the previous alert)
            const last = tab.messages[tab.messages.length - 1]
            const isLastMessage = last && last.message_type === 'new-loads-alert' && last.surface === 'search' && last.id.startsWith('new-loads-live-')
            const searchStreaming = tab.searchRuntime.isStreaming || tab.searchRuntime.streamingEventLog.length > 0
            if (isLastMessage && !searchStreaming) {
                tab.messages[tab.messages.length - 1] = { ...last, content: action.payload.content }
            } else {
                tab.messages.push({
                    id: `new-loads-live-${Date.now()}`,
                    message_type: 'new-loads-alert',
                    surface: 'search',
                    content: action.payload.content,
                    created_at: new Date().toISOString(),
                })
            }
        },

        prependMessages(state, action: PayloadAction<{ tabId: string; messages: AgenticMessage[]; hasMore: boolean; page: number; surface?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const surface = action.payload.surface || 'chat'
                const existingSurfaceMessages = tab.messages.filter((message) => (message.surface || 'chat') === surface)
                const olderMessages = action.payload.messages.map((message) => ({ ...message, surface: message.surface || surface }))
                const mergedSurfaceMessages = sortMessagesChronologically([...olderMessages, ...existingSurfaceMessages])
                const otherMessages = tab.messages.filter((message) => (message.surface || 'chat') !== surface)
                tab.messages = sortMessagesChronologically([...otherMessages, ...mergedSurfaceMessages])
                const pagination = getMessagePagination(tab, surface)
                pagination.hasMore = action.payload.hasMore
                pagination.page = action.payload.page
                pagination.loading = false
                if (surface === 'chat') syncChatMessagePaginationAliases(tab)
            }
        },

        setMessagesPage(state, action: PayloadAction<{ tabId: string; page: number; hasMore: boolean; surface?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const surface = action.payload.surface || 'chat'
                const pagination = getMessagePagination(tab, surface)
                pagination.page = action.payload.page
                pagination.hasMore = action.payload.hasMore
                if (surface === 'chat') syncChatMessagePaginationAliases(tab)
            }
        },

        setLoadingMoreMessages(state, action: PayloadAction<{ tabId: string; loading: boolean; surface?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const surface = action.payload.surface || 'chat'
                getMessagePagination(tab, surface).loading = action.payload.loading
                if (surface === 'chat') syncChatMessagePaginationAliases(tab)
            }
        },

        setToolCallStart(
            state,
            action: PayloadAction<{
                tabId: string
                toolCallId: string
                toolName: string
                toolInput: string
                lane?: AgenticSurface
                /** When set, this call originated inside a subagent invocation and should nest under
                 *  `parentToolCallId` instead of rendering as a top-level event. */
                subagent?: string
                parentToolCallId?: string
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                runtime.activeToolCalls[action.payload.toolCallId] = {
                    toolName: action.payload.toolName,
                    toolInput: action.payload.toolInput,
                    status: 'executing',
                    subagent: action.payload.subagent,
                    parentToolCallId: action.payload.parentToolCallId,
                    startedAtMs: Date.now(),
                }
                if (action.payload.parentToolCallId) {
                    // Nested under parent's `task` card — append to that parent's ordered `events`
                    // list. We deliberately skip `streamingEventLog.push` so this call doesn't
                    // render at the top of the chat lane. Settle any prior streaming thinking on
                    // the parent so its cursor blink stops as the new tool kicks off. If the
                    // parent isn't in `activeToolCalls` (out-of-order delivery — rare but
                    // possible), fall back to top-level rendering so the call doesn't disappear.
                    const parent = runtime.activeToolCalls[action.payload.parentToolCallId]
                    if (parent) {
                        if (!parent.events) parent.events = []
                        settleStreamingThinking(parent.events)
                        parent.events.push({ kind: 'tool', toolCallId: action.payload.toolCallId })
                    } else {
                        runtime.streamingEventLog.push({ kind: 'tool-call', toolCallId: action.payload.toolCallId })
                    }
                } else {
                    runtime.streamingEventLog.push({ kind: 'tool-call', toolCallId: action.payload.toolCallId })
                }
                capEventLog(runtime)
                if (lane === 'chat') syncChatRuntimeAliases(tab)
            }
        },

        setToolCallResult(state, action: PayloadAction<{ tabId: string; toolCallId: string; toolOutput: string; success: boolean; lane?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const lane = action.payload.lane || 'chat'
                const runtime = getLaneRuntime(tab, lane)
                if (!runtime.activeToolCalls[action.payload.toolCallId]) return
                runtime.activeToolCalls[action.payload.toolCallId].status = 'complete'
                runtime.activeToolCalls[action.payload.toolCallId].toolOutput = action.payload.toolOutput
                runtime.activeToolCalls[action.payload.toolCallId].success = action.payload.success
                if (lane === 'chat') syncChatRuntimeAliases(tab)
            }
        },

        setLoads(state, action: PayloadAction<{ tabId: string; loads: CanonicalSearchLoad[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                // `sent_email_body` and the sync highlight need no preserving — they live in `_rowUi`
                // beside the rows, so replacing `tab.loads` leaves them alone.
                // Preserve latest_broker_replied_at — optimistically set by AH_EmailUpdated
                // (incrementLoadUnread) when a broker reply arrives. The API response may not
                // round-trip this field on subsequent fetches, which would wipe the With Response
                // tab membership and the broker-replied tier promotion. Keep the optimistic value
                // if the incoming payload doesn't carry one.
                const repliedAtMap = new Map<number, string>()
                for (const old of tab.loads) {
                    if (old.latestBrokerRepliedAt) repliedAtMap.set(old.rowNumber, old.latestBrokerRepliedAt)
                }

                // Build/update persistent factoring grade cache from current loads before replacing
                if (!tab._factoringCache) tab._factoringCache = {}
                for (const old of tab.loads) {
                    if (old.factoringGrade) {
                        tab._factoringCache[old.rowNumber] = {
                            grade: old.factoringGrade,
                            source: old.factoringSource,
                            loadsCount: old.factoringLoadsCount,
                            color: old.factoringColor,
                        }
                    }
                }
                // Also seed cache from incoming loads that already have grades (e.g. from DB)
                for (const incoming of action.payload.loads) {
                    if (incoming.factoringGrade) {
                        tab._factoringCache[incoming.rowNumber] = {
                            grade: incoming.factoringGrade,
                            source: incoming.factoringSource,
                            loadsCount: incoming.factoringLoadsCount,
                            color: incoming.factoringColor,
                        }
                    }
                }

                for (const load of action.payload.loads) {
                    if (!load.latestBrokerRepliedAt && repliedAtMap.has(load.rowNumber)) {
                        load.latestBrokerRepliedAt = repliedAtMap.get(load.rowNumber)
                    }
                    // Restore factoring grades from persistent cache
                    if (!load.factoringGrade && tab._factoringCache[load.rowNumber]) {
                        load.factoringGrade = tab._factoringCache[load.rowNumber].grade
                        load.factoringSource = tab._factoringCache[load.rowNumber].source
                        load.factoringLoadsCount = tab._factoringCache[load.rowNumber].loadsCount
                        load.factoringColor = tab._factoringCache[load.rowNumber].color
                    }
                    // Search-loaded loads: bold text but no bg/border highlight
                    if (load.isRead === undefined) load.isRead = false
                }
                // Preserve the load currently being viewed in the email sidebar so the thread
                // doesn't disappear during refetches. When page 1 is refetched, a load on page 2+
                // would otherwise vanish from tabState.loads, breaking the email rail.
                // ponytail: spread to new array, don't mutate payload (caller reads .length after dispatch)
                const incomingIds = new Set(action.payload.loads.map((l) => l.id))
                let finalLoads = action.payload.loads
                if (state.activeEmailLoadId && state.activeEmailTabId === action.payload.tabId && !incomingIds.has(state.activeEmailLoadId)) {
                    const activeLoad = tab.loads.find((l) => l.id === state.activeEmailLoadId)
                    if (activeLoad) {
                        finalLoads = [...action.payload.loads, activeLoad]
                    }
                }
                tab.loads = finalLoads
                tab.loadsLoading = false
                recalcUnreadByEmail(state)
            }
        },

        appendLoads(state, action: PayloadAction<{ tabId: string; loads: CanonicalSearchLoad[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const existingIds = new Set(tab.loads.map((l) => l.rowNumber))
                const newLoads = action.payload.loads.filter((l) => !existingIds.has(l.rowNumber))
                tab.loads = [...tab.loads, ...newLoads]
            }
        },

        setLoadsLoading(state, action: PayloadAction<{ tabId: string; loading: boolean }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.loadsLoading = action.payload.loading
        },

        /** Server-reported `pagination.total` from any /loads call. Called by
         *  both the paginated fetch (filter change, manual reload) and the
         *  per-SSE-batch reload so the table header counter ticks up live as
         *  the search streams. Pass `null` to clear (tab switch, filter
         *  flip — before the new total arrives). */
        setTotalLoadsCount(state, action: PayloadAction<{ tabId: string; total: number | null }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.totalLoadsCount = action.payload.total
        },

        /** Decrement `totalLoadsCount` by 1, floored at 0. Used after the
         *  dispatcher manually deletes / restores a single load so the header
         *  counter reflects the new client-side reality without a /loads
         *  round-trip. No-op when the count is `null` (never fetched). */
        decrementTotalLoadsCount(state, action: PayloadAction<{ tabId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab && tab.totalLoadsCount !== null) tab.totalLoadsCount = Math.max(0, tab.totalLoadsCount - 1)
        },

        setLoadsSearchQuery(state, action: PayloadAction<{ tabId: string; query: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.loadsSearchQuery = action.payload.query
        },

        setStatusFilter(state, action: PayloadAction<{ tabId: string; statuses: string[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.statusFilter = action.payload.statuses
        },

        setResponseFilter(state, action: PayloadAction<{ tabId: string; filter: ResponseFilter }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.responseFilter = action.payload.filter
        },

        addLoad(state, action: PayloadAction<{ tabId: string; load: CanonicalSearchLoad }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab && !tab.loads.some((l) => l.rowNumber === action.payload.load.rowNumber)) {
                tab.loads.unshift(action.payload.load)
                // A row pushed live (a new posting, an outreach load) is one more on the board.
                if (tab.totalLoadsCount !== null && !action.payload.load.deletedAt && countsLiveBoard(tab)) tab.totalLoadsCount += 1
            }
        },

        // Insert-or-merge by row_number. Used when recovering a load on demand
        // (e.g. clicking an attention item whose load isn't in the current page) —
        // we must not duplicate an existing entry, but a stale one should be refreshed
        // so its fields (email_thread_id, broker_name, etc.) match the server.
        upsertLoad(state, action: PayloadAction<{ tabId: string; load: CanonicalSearchLoad }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const existing = tab.loads.find((l) => l.rowNumber === action.payload.load.rowNumber)
            if (existing) {
                Object.assign(existing, action.payload.load)
            } else {
                tab.loads.unshift(action.payload.load)
            }
        },

        addLoads(state, action: PayloadAction<{ tabId: string; loads: CanonicalSearchLoad[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const existing = new Set(tab.loads.map((l) => l.rowNumber))
                const newLoads: CanonicalSearchLoad[] = []
                for (const load of action.payload.loads) {
                    if (!existing.has(load.rowNumber)) {
                        newLoads.push(load)
                        existing.add(load.rowNumber)
                    }
                }
                tab.loads.unshift(...newLoads)
                // Rows pushed live (a burst of new postings) are that many more on the board.
                const added = newLoads.filter((load) => !load.deletedAt).length
                if (added > 0 && tab.totalLoadsCount !== null && countsLiveBoard(tab)) tab.totalLoadsCount += added
            }
        },

        updateLoad(state, action: PayloadAction<{ tabId: string; rowNumber: number; updates: BoardUpdateFields }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                // `sent_email_body` rides the same socket bag but is not a load field — the send is not in
                // the load read until Nylas indexes it. Route it to the row's UI state, not onto the row.
                const loadUpdates = { ...action.payload.updates }
                const sentEmailBody = loadUpdates.sent_email_body
                delete loadUpdates.sent_email_body
                delete loadUpdates.keepVisible
                if (sentEmailBody !== undefined) rowUi(tab, action.payload.rowNumber).sentEmailBody = sentEmailBody
                const load = tab.loads.find((l) => l.rowNumber === action.payload.rowNumber)
                if (load) Object.assign(load, loadUpdates)
                // Keep factoring cache in sync
                if (action.payload.updates.factoringGrade) {
                    if (!tab._factoringCache) tab._factoringCache = {}
                    tab._factoringCache[action.payload.rowNumber] = {
                        grade: action.payload.updates.factoringGrade,
                        source: action.payload.updates.factoringSource,
                        loadsCount: action.payload.updates.factoringLoadsCount,
                        color: action.payload.updates.factoringColor,
                    }
                }
            }
        },

        /** The dispatcher looked at the row — stop flashing it. */
        clearRowHighlight(state, action: PayloadAction<{ tabId: string; rowNumber: number }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab?._rowUi?.[action.payload.rowNumber]) tab._rowUi[action.payload.rowNumber].highlight = false
        },

        batchUpdateLoads(
            state,
            action: PayloadAction<{
                tabId: string
                updates: Array<{ rowNumber: number; updates: Partial<CanonicalSearchLoad> }>
                removeRowNumbers: number[]
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            // Build index once — O(n) — then apply all updates in O(1) each
            const byRowNumber = new Map(tab.loads.map((l) => [l.rowNumber, l]))
            // A row archived live (a cancelled posting) leaves the board; one brought back (a repost) rejoins it.
            let liveDelta = 0
            for (const { rowNumber, updates } of action.payload.updates) {
                const load = byRowNumber.get(rowNumber)
                if (!load) continue
                const wasLive = !load.deletedAt
                Object.assign(load, updates)
                if (wasLive !== !load.deletedAt) liveDelta += wasLive ? -1 : 1
            }
            if (liveDelta !== 0 && tab.totalLoadsCount !== null && countsLiveBoard(tab)) tab.totalLoadsCount = Math.max(0, tab.totalLoadsCount + liveDelta)
            if (action.payload.removeRowNumbers.length > 0) {
                const toRemove = new Set(action.payload.removeRowNumbers)
                tab.loads = tab.loads.filter((l) => !toRemove.has(l.rowNumber))
            }
        },

        incrementLoadUnread(state, action: PayloadAction<{ tabId: string; emailThreadId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                // Page-independent tab badge count — bump even if the load isn't on the loaded page
                // (or the tab was never opened), so background tabs reflect a new broker reply.
                tab.unreadCount = (tab.unreadCount ?? 0) + 1
                const load = tab.loads.find((l) => l.emailThreadId === action.payload.emailThreadId)
                if (load) {
                    load.unreadCount = (load.unreadCount ?? 0) + 1
                    load.isRead = false
                    rowUi(tab, load.rowNumber).highlight = true
                    // Set latest_broker_replied_at optimistically so the client-side sectionTier()
                    // immediately promotes this load to tier 0 without waiting for a full refetch.
                    // The exact timestamp doesn't need to match Nylas — non-null is all that matters
                    // for the tier check. The accurate server value overwrites it on the next fetchLoads.
                    if (!load.latestBrokerRepliedAt) load.latestBrokerRepliedAt = new Date().toISOString()
                }
                recalcUnreadByEmail(state)
            }
        },

        bumpEmailVersion(state, action: PayloadAction<{ tabId: string; emailThreadId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            const load = tab?.loads.find((l) => l.emailThreadId === action.payload.emailThreadId)
            if (load) {
                const ui = rowUi(tab, load.rowNumber)
                ui.emailVersion = (ui.emailVersion ?? 0) + 1
                return
            }
            // No matching load — this is a broker-outreach inquiry thread (lives in agent_inquiries,
            // not agent_loads). Bump the inquiry-keyed counter so an open BrokerInquiryPanel refetches.
            state.inquiryEmailVersions[action.payload.emailThreadId] = (state.inquiryEmailVersions[action.payload.emailThreadId] ?? 0) + 1
        },

        resetLoadUnread(state, action: PayloadAction<{ tabId: string; rowNumber: number }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const load = tab.loads.find((l) => l.rowNumber === action.payload.rowNumber)
                if (load) {
                    // Drop this thread's unread from the page-independent tab count before zeroing it.
                    tab.unreadCount = Math.max(0, (tab.unreadCount ?? 0) - (load.unreadCount ?? 0))
                    load.unreadCount = 0
                    load.isRead = true
                }
                recalcUnreadByEmail(state)
            }
        },

        addCallToLoad(state, action: PayloadAction<{ tabId: string; postingId: string; callId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const load = tab.loads.find((l) => l.externalId === action.payload.postingId)
                if (load) {
                    if (!load.callHistory) load.callHistory = []
                    if (!load.callHistory.some((c) => c.callId === action.payload.callId)) {
                        load.callHistory.push({ callId: action.payload.callId })
                    }
                }
            }
        },

        /**
         * Apply end-of-call outputs to a load row. Looks up the load by `external_load_id` so the
         * AH_CallCompleted socket handler doesn't need to know the row number. Mirrors the structured
         * fields from `collectedInfo` (rate, commodity, weight, special_requirements, pickup/delivery
         * times) onto the row — same field-mapping logic that useAgenticHubCall.ts:142-162 does
         * locally, kept in sync with the server-side `updateLoadFromVoiceToolParams` helper.
         */
        applyCallResult(
            state,
            action: PayloadAction<{ tabId: string; postingId: string; resultText: string; resultAt: string; collectedInfo?: Record<string, unknown> }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const load = tab.loads.find((l) => l.externalId === action.payload.postingId)
            if (!load) return
            load.lastCallResult = action.payload.resultText
            load.lastCallResultAt = action.payload.resultAt
            const info = action.payload.collectedInfo
            if (info) {
                if (info.rate !== undefined && info.rate !== null) {
                    const rate = Number(String(info.rate).replace(/[^\d.]/g, ''))
                    if (Number.isFinite(rate) && rate > 0) load.rateAmountUsd = rate
                }
                if (info.commodity) load.commodity = String(info.commodity)
                if (info.weight) {
                    const w = parseInt(String(info.weight).replace(/[^\d]/g, ''), 10)
                    if (Number.isFinite(w) && w > 0) load.weightLbs = w
                }
                if (info.specialRequirements) load.specialRequirements = String(info.specialRequirements)
            }
        },

        removeLoads(state, action: PayloadAction<{ tabId: string; rowNumbers: number[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                tab.loads = tab.loads.filter((l) => !action.payload.rowNumbers.includes(l.rowNumber))
            }
        },

        addQueuedInput(state, action: PayloadAction<{ tabId: string; input: QueuedInput }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.queuedInputs.push(action.payload.input)
        },

        clearQueuedInputs(state, action: PayloadAction<{ tabId: string; lane?: AgenticSurface }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            if (action.payload.lane) {
                if (action.payload.lane === 'chat') {
                    const chatQueued = tab.queuedInputs.filter((item) => (item.lane || 'chat') === 'chat' && item.source === 'user')
                    for (const item of chatQueued) {
                        tab.messages.push({
                            id: `queued-user-${Date.now()}-${item.position}`,
                            message_type: 'user',
                            surface: 'chat',
                            content: item.content || item.preview,
                            created_at: new Date().toISOString(),
                        })
                    }
                }
                tab.queuedInputs = tab.queuedInputs.filter((item) => (item.lane || 'chat') !== action.payload.lane)
                return
            }
            tab.queuedInputs = []
        },

        setSearchBarCollapsed(state, action: PayloadAction<{ tabId: string; collapsed: boolean }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.searchBarCollapsed = action.payload.collapsed
        },

        setPendingRequests(state, action: PayloadAction<{ tabId: string; requests: AgentRequest[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                tab.pendingRequests = [...action.payload.requests].sort((a, b) => {
                    const left = a.created_at ? new Date(a.created_at).getTime() : 0
                    const right = b.created_at ? new Date(b.created_at).getTime() : 0
                    return left - right
                })
                // Full request set now present → pending is counted live from it (not the startup seed).
                tab.requestsFetched = true
            }
        },

        addPendingRequest(state, action: PayloadAction<{ tabId: string; request: AgentRequest }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const newReq = action.payload.request
                // Remove any existing request with same id OR same resource_keys (toolCallId → requestId upgrade)
                const resourceKeys = newReq.resource_keys ?? []
                tab.pendingRequests = tab.pendingRequests.filter((r) => {
                    if (r.id === newReq.id) return false
                    if (resourceKeys.length > 0 && r.resource_keys?.some((k) => resourceKeys.includes(k))) return false
                    return true
                })
                tab.pendingRequests.push(newReq)
            }
        },

        resolvePendingRequest(state, action: PayloadAction<{ tabId: string; requestId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            tab.pendingRequests = tab.pendingRequests.filter((request) => request.id !== action.payload.requestId)
        },

        /** Persist the in-progress answers for a chat-question batch so they survive the carousel
         *  remounting across pill expand/collapse. */
        setChatQuestionDraft(state, action: PayloadAction<{ tabId: string; correlationId: string; draft: ChatQuestionDraft }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            tab.chatQuestionDrafts[action.payload.correlationId] = action.payload.draft
        },

        /** Drop a batch's staged answers once it's submitted/skipped (or no longer needed). */
        clearChatQuestionDraft(state, action: PayloadAction<{ tabId: string; correlationId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            delete tab.chatQuestionDrafts[action.payload.correlationId]
        },

        updateTabTitle(state, action: PayloadAction<{ tabId: string; text: string; done?: boolean }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                tab.tab.title = action.payload.text
            }
        },

        setSearchForm(state, action: PayloadAction<{ tabId: string; searchForm: (SearchFormValues & { latestLoadPostedAt?: number }) | null }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.searchForm = action.payload.searchForm
        },

        /** Shallow-merge a partial search-form patch into the tab's stored form
         *  and bump `searchFormSyncNonce` so the mounted form re-hydrates. Used by
         *  the `AH_SearchFormUpdated` socket event (chat-agent `scoring` tool wrote
         *  new filters). Patch is already in FE `SearchFormValues` shape (string
         *  values, FE keys). No-op when the tab has no form yet AND the patch is
         *  empty; when the form is null we seed it from the patch so the keys still
         *  land (the next full search/hydrate fills the rest). */
        mergeSearchForm(state, action: PayloadAction<{ tabId: string; patch: Record<string, unknown>; cleared?: string[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const patch = action.payload.patch ?? {}
            const cleared = action.payload.cleared ?? []
            // A turn that replaces a criterion names it in both, so the patch wins and only
            // the keys it does NOT carry are removed.
            const removed = cleared.filter((key) => !(key in patch))
            // A cleared-only turn on a tab with no form yet has nothing to delete, and seeding
            // `{}` would be worse than doing nothing: `{}` is truthy, so it passes the sync
            // effect's null guard, and `searchFormToLoadFilters({})` returns POPULATED defaults
            // that overwrite an origin or date range the dispatcher typed but hasn't searched.
            if (Object.keys(patch).length === 0 && (removed.length === 0 || !tab.searchForm)) return
            const next = { ...(tab.searchForm ?? {}), ...patch } as TabState['searchForm']
            // `Partial` is what makes the delete legal: every field on the form type is
            // required, and a removed one is simply absent.
            const removable = next as Partial<SearchFormValues>
            for (const key of removed) delete removable[key as keyof SearchFormValues]
            tab.searchForm = next
            tab.clearedFormKeys = removed
            tab.searchFormSyncNonce += 1
        },

        /** REPLACE the tab's stored form wholesale and bump `searchFormReplaceNonce`.
         *  Used by the recent-search prefill: unlike mergeSearchForm, the mounted form
         *  fully RESETS its advanced inputs from this form (see searchFormReplaceNonce),
         *  so a previously-selected search's RPM/rate/miles/prefs don't bleed into the
         *  newly-restored one. */

        /** Mark a tab as having completed a search by setting latestLoadPostedAt.
         *  This is the client-side signal that gates the poll UI (auto/manual sync
         *  controls). Backend persists the same field to search_form in the DB on
         *  background completion, but we need an immediate signal so the UI doesn't
         *  wait for a page refresh to show the poll controls. */
        markTabSearched(state, action: PayloadAction<{ tabId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            tab.hasSearched = true
            if (tab.searchForm) {
                tab.searchForm = { ...tab.searchForm, latestLoadPostedAt: Date.now() }
            }
        },

        /** Record the FIRST search's end-to-end SSE latency. Idempotent — once set,
         *  later searches don't overwrite (the value is meant to surface cold-start
         *  / first-impression latency, not rolling latency). */
        setFirstSearchLatency(state, action: PayloadAction<{ tabId: string; latencyMs: number }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            if (tab.firstSearchLatencyMs != null) return
            tab.firstSearchLatencyMs = action.payload.latencyMs
        },

        setProcessingSearch(
            state,
            action: PayloadAction<{
                tabId: string
                processingSearch: { active: boolean } | null
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.processingSearch = action.payload.processingSearch
        },

        setAnalyzingBoard(
            state,
            action: PayloadAction<{
                tabId: string
                analyzingBoard: { active: boolean; phase?: 'processing' | 'analyzing'; loadedCount?: number; totalAvailable?: number } | null
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            // New search starts in `'processing'` phase — the BE is paginating loads.
            // `all_sources_complete` flips it to `'analyzing'` via `setAnalyzingBoardPhase`.
            tab.analyzingBoard = action.payload.analyzingBoard
                ? { ...action.payload.analyzingBoard, phase: action.payload.analyzingBoard.phase ?? 'processing' }
                : null
            // New search starting — clear any stale "Analyzing answers"
            // signal from a prior batch so the chat pill resets cleanly.
            if (action.payload.analyzingBoard?.active) {
                tab.analyzingAnswers = false
            }
        },

        /** Flip the analyzing-board pill from `'processing'` (paginating loads, ring
         *  drawn around pill) to `'analyzing'` (pagination done, indeterminate dots-only).
         *  Fires on `all_sources_complete` socket event under v2. No-op when the pill
         *  isn't active. */
        setAnalyzingBoardPhase(state, action: PayloadAction<{ tabId: string; phase: 'processing' | 'analyzing' }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab || !tab.analyzingBoard?.active) return
            tab.analyzingBoard = { ...tab.analyzingBoard, phase: action.payload.phase }
        },

        /** Bump the processing-phase pagination counters from the search SSE
         *  socket event. No-op when the loader isn't active OR has already flipped
         *  to `'analyzing'` (a late event must not redraw the progress ring after
         *  the pill has already transitioned). */
        setAnalyzingBoardProgress(state, action: PayloadAction<{ tabId: string; loadedCount: number; totalAvailable: number }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab || !tab.analyzingBoard?.active || tab.analyzingBoard.phase !== 'processing') return
            tab.analyzingBoard = {
                ...tab.analyzingBoard,
                loadedCount: action.payload.loadedCount,
                totalAvailable: action.payload.totalAvailable,
            }
        },

        /** Drives the collapsed-pill "Analyzing answers" state. Set true when
         *  the dispatcher submits a chat-agent question batch; cleared when
         *  the BE emits `autonomous_run_complete` for the answer-triggered
         *  run. Also auto-cleared on a fresh search via `setAnalyzingBoard`. */
        setAnalyzingAnswers(state, action: PayloadAction<{ tabId: string; value: boolean }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.analyzingAnswers = action.payload.value
        },

        /** Mark a chat-agent question batch as having arrived via live socket
         *  emit THIS session. ChatPill's auto-popup gates on this list so a
         *  page-refresh rehydrate doesn't trigger the popup for old batches.
         *  Idempotent — duplicate emits (broker retries, etc.) don't double-add. */
        markChatQuestionLive(state, action: PayloadAction<{ tabId: string; correlationId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            if (!tab.liveChatQuestionCorrelationIds.includes(action.payload.correlationId)) {
                tab.liveChatQuestionCorrelationIds.push(action.payload.correlationId)
            }
        },

        /** Drop a correlationId from the live set — used when the dispatcher
         *  closes the auto-popup via the X button. The underlying pending
         *  requests stay untouched (they're still pending on the BE); the
         *  dispatcher can still answer them by expanding the chat card. */
        dismissLiveChatQuestion(state, action: PayloadAction<{ tabId: string; correlationId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            tab.liveChatQuestionCorrelationIds = tab.liveChatQuestionCorrelationIds.filter((id) => id !== action.payload.correlationId)
        },

        setScoringStartedAt(state, action: PayloadAction<{ tabId: string; scoringStartedAt: string | null }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.scoringStartedAt = action.payload.scoringStartedAt
        },

        /** Track the BE-stamped autonomous_started_at so the FE tick can compute
         *  the 5h cutoff locally (avoids waiting up to 10min for the next heartbeat). */
        setAutonomousStartedAt(state, action: PayloadAction<{ tabId: string; autonomousStartedAt: string | null }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.autonomousStartedAt = action.payload.autonomousStartedAt
        },

        /** Flip on when wakeup returns reason='autonomous_expired' or when the
         *  local 5h tick fires. Flip off only via resetAutonomousRun (Resume button). */
        setAutonomousExpired(state, action: PayloadAction<{ tabId: string; expired: boolean }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.autonomousExpired = action.payload.expired
        },

        /** Clears expiry + re-stamps started_at after the BE reset endpoint succeeds.
         *  Single action so both fields move together — no transient state where
         *  expired=false but startedAt is still the old expired value. */
        resumeAutonomousRun(state, action: PayloadAction<{ tabId: string; autonomousStartedAt: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            tab.autonomousExpired = false
            tab.autonomousStartedAt = action.payload.autonomousStartedAt
        },

        setScoringProgress(state, action: PayloadAction<{ tabId: string; progress: { processed: number; total: number } | null }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) tab.scoringProgress = action.payload.progress
        },

        setLoadStreamingState(
            state,
            action: PayloadAction<{
                tabId: string
                postingId: string
                phase: 'thinking' | 'tool-call' | 'approval-pending' | 'done'
                toolName?: string
                text?: string
                finalContent?: string
                request?: { id: string; title: string; body: string; options: { id: string; label: string }[] }
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const { postingId, phase, toolName, text, finalContent, request } = action.payload
            const existing = tab.loadStreamingStates[postingId]

            // Protect approval-pending: only allow approval-pending (update requestId) through.
            // Previously we also allowed phase:'done' so AH_StreamEnd could clear the pill, but
            // this races with AH_RequestCreated on agent retry — the new request lands while the
            // load is already cleared. The approval pill is resolved exclusively by the user's
            // response now; the stream-end signal is a no-op while we're awaiting input.
            if (existing?.phase === 'approval-pending' && phase !== 'approval-pending') {
                return
            }

            if (phase === 'done') {
                if (finalContent) {
                    tab.loadStreamingStates[postingId] = { ...existing, phase, finalContent, startedAt: Date.now() }
                } else {
                    delete tab.loadStreamingStates[postingId]
                }
            } else {
                tab.loadStreamingStates[postingId] = {
                    phase,
                    toolName: toolName ?? existing?.toolName,
                    text: text ?? existing?.text,
                    request: request ?? existing?.request,
                    // Refresh on every active-phase update so the watchdog measures *inactivity*,
                    // not total run time. A 5s gap with no new stream event = treat as stale and
                    // fall back to the load's real status.
                    startedAt: Date.now(),
                }
            }
        },

        clearLoadStreamingState(state, action: PayloadAction<{ tabId: string; postingId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) delete tab.loadStreamingStates[action.payload.postingId]
        },

        // Per-load supersede: written when AH_Superseded fires for an email-source turn keyed by
        // postingId. Stops the active spinner (phase='done') and flags `superseded` so the row
        // renders a gray "AI stopped" pill (distinct from the red "AI error" pill). Cleared
        // implicitly when the next AH_StreamStart for the same posting replaces the entry, or
        // explicitly via clearLoadStreamingState.
        setLoadSuperseded(state, action: PayloadAction<{ tabId: string; postingId: string }>) {
            const { tabId, postingId } = action.payload
            const tab = state.tabs[tabId]
            if (!tab) return
            const existing = tab.loadStreamingStates[postingId]
            // Same approval-pending guard as setLoadStreamingError — don't clobber a pill the
            // dispatcher still needs to answer.
            if (existing?.phase === 'approval-pending') return
            tab.loadStreamingStates[postingId] = {
                phase: 'done',
                toolName: existing?.toolName,
                text: existing?.text,
                request: existing?.request,
                startedAt: existing?.startedAt ?? Date.now(),
                superseded: true,
            }
        },

        // Per-load error: written when AH_Error fires for an email-source turn keyed by postingId.
        // Stops the active spinner (phase='done') and stores the message so the row can render an
        // error pill instead of clearing silently. Auto-cleared by LoadsPanel after a few seconds.
        setLoadStreamingError(state, action: PayloadAction<{ tabId: string; postingId: string; message: string }>) {
            const { tabId, postingId, message } = action.payload
            const tab = state.tabs[tabId]
            if (!tab) return
            const existing = tab.loadStreamingStates[postingId]
            // Mirror the approval-pending guard from setLoadStreamingState — if the row is
            // awaiting dispatcher input, an AH_Error (e.g. EMAIL_AGENT_LLM_TIMEOUT) would
            // otherwise overwrite the approval pill and leave the dispatcher unable to answer
            // the still-open question. The pending request resolves via the user's response
            // (or AH_RequestDismissed, which uses clearLoadStreamingState to bypass).
            if (existing?.phase === 'approval-pending') return
            tab.loadStreamingStates[postingId] = {
                phase: 'done',
                toolName: existing?.toolName,
                text: existing?.text,
                request: existing?.request,
                startedAt: existing?.startedAt ?? Date.now(),
                error: message,
            }
        },

        // Broker-outreach equivalent of setLoadStreamingState. Keyed by threadId since the agent
        // reasons over the whole email thread, not a single load. The render layer joins this map
        // to load rows by `email_thread_id`, so all loads in the active thread share the spinner.
        setThreadStreamingState(
            state,
            action: PayloadAction<{
                tabId: string
                threadId: string
                phase: 'thinking' | 'tool-call' | 'done'
                toolName?: string
                text?: string
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const { threadId, phase, toolName, text } = action.payload
            const existing = tab.threadStreamingStates[threadId]
            if (phase === 'done') {
                delete tab.threadStreamingStates[threadId]
                return
            }
            tab.threadStreamingStates[threadId] = {
                phase,
                toolName: toolName ?? existing?.toolName,
                text: text ?? existing?.text,
                // Refresh on every active-phase update so the watchdog measures inactivity, not
                // total run time (mirrors setLoadStreamingState).
                startedAt: Date.now(),
            }
        },

        clearThreadStreamingState(state, action: PayloadAction<{ tabId: string; threadId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) delete tab.threadStreamingStates[action.payload.threadId]
        },

        transitionLoadsToRanking(state, action: PayloadAction<{ tabId: string }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                for (const load of tab.loads) {
                    if (load.emailStatus === 'processing') load.emailStatus = 'ranking'
                }
            }
        },

        addSyncLoads(state, action: PayloadAction<{ tabId: string; loads: CanonicalSearchLoad[] }>) {
            const tab = state.tabs[action.payload.tabId]
            if (tab) {
                const existing = new Set(tab.loads.map((l) => l.rowNumber))
                const newLoads: CanonicalSearchLoad[] = []
                for (const load of action.payload.loads) {
                    if (!existing.has(load.rowNumber)) {
                        rowUi(tab, load.rowNumber).highlight = true
                        newLoads.push(load)
                        existing.add(load.rowNumber)
                    }
                }
                if (newLoads.length > 0) tab.loads.unshift(...newLoads)
            }
        },

        finalizeStream(
            state,
            action: PayloadAction<{
                tabId: string
                messageId: string
                finalContent: string
                inputTokens?: number
                outputTokens?: number
                lane?: AgenticSurface
            }>
        ) {
            const tab = state.tabs[action.payload.tabId]
            if (!tab) return
            const lane = action.payload.lane || 'chat'
            const runtime = getLaneRuntime(tab, lane)
            const surface: AgenticSurface = lane === 'search' ? 'search' : 'chat'

            // Dedup against rows already in the tab (the StreamStart refetch / watchdog fallback may
            // have fetched some via API). Dedup by id first, then by `messageDedupKey` — which keys
            // tool rows on tool_call_id (their content is empty, so a content key drops every tool
            // call after the first → the call shows live then vanishes on finalize until refresh).
            const existingIds = new Set(tab.messages.map((m) => m.id))
            const existingKeys = new Set(tab.messages.map(messageDedupKey).filter((k): k is string => k !== null))
            const pushIfNew = (msg: AgenticMessage) => {
                if (existingIds.has(msg.id)) return
                const key = messageDedupKey(msg)
                if (key !== null && existingKeys.has(key)) return
                existingIds.add(msg.id)
                if (key !== null) existingKeys.add(key)
                tab.messages.push(msg)
            }

            // Replay streaming events in the order they occurred (preserves interleaved order)
            const now = new Date().toISOString()
            let thinkIdx = 0
            let textIdx = 0
            let hasTextEvents = false
            for (const event of runtime.streamingEventLog) {
                if (event.kind === 'thinking') {
                    pushIfNew({
                        id: `${action.payload.messageId}-thinking-${thinkIdx++}`,
                        message_type: 'agent-thinking',
                        surface,
                        content: event.content,
                        created_at: now,
                    })
                } else if (event.kind === 'tool-call') {
                    const tc = runtime.activeToolCalls[event.toolCallId]
                    if (!tc) continue
                    pushIfNew({
                        id: `${event.toolCallId}-call`,
                        message_type: 'agent-tool-call',
                        surface,
                        content: '',
                        tool_name: tc.toolName,
                        tool_input: tc.toolInput,
                        tool_call_id: event.toolCallId,
                        created_at: now,
                    })
                    if (tc.status === 'complete' || tc.status === 'error') {
                        pushIfNew({
                            id: `${event.toolCallId}-result`,
                            message_type: 'agent-tool-result',
                            surface,
                            content: tc.toolOutput ?? '',
                            tool_name: tc.toolName,
                            tool_success: tc.success,
                            tool_call_id: event.toolCallId,
                            created_at: now,
                        })
                    }
                } else if (event.kind === 'text') {
                    if (!event.content.trim()) continue
                    hasTextEvents = true
                    pushIfNew({
                        id: `${action.payload.messageId}-text-${textIdx++}`,
                        message_type: 'agent-response',
                        surface,
                        content: event.content,
                        input_tokens: action.payload.inputTokens,
                        output_tokens: action.payload.outputTokens,
                        created_at: now,
                    })
                }
            }

            // Fallback: use finalContent only if no text events were logged and content is non-empty
            if (!hasTextEvents && action.payload.finalContent?.trim()) {
                pushIfNew({
                    id: action.payload.messageId,
                    message_type: 'agent-response',
                    surface,
                    content: action.payload.finalContent,
                    input_tokens: action.payload.inputTokens,
                    output_tokens: action.payload.outputTokens,
                    created_at: now,
                })
            }

            // Clear streaming state
            runtime.streamingText = ''
            runtime.streamingThinking = ''
            runtime.streamingMessageId = null
            runtime.isSending = false
            runtime.isStreaming = false
            runtime.activeToolCalls = {}
            runtime.streamingEventLog = []
            if (lane === 'chat') syncChatRuntimeAliases(tab)
            // Don't clear queuedInputs here — AH_BatchProcessingStart dispatches clearQueuedInputs
            // when the agent starts processing queued items. Clearing here would wipe the queue
            // indicator before batch processing begins.
        },
        setRoutedMiles(state, action: PayloadAction<{ loadId: string; tripMiles: number | null; deadheadMiles: number | null }>) {
            const { loadId, tripMiles, deadheadMiles } = action.payload
            state.routedMiles[loadId] = { tripMiles, deadheadMiles }
        },
        clearRoutedMiles(state, action: PayloadAction<string>) {
            delete state.routedMiles[action.payload]
        },
    },
})

export const {
    clearRowHighlight,
    addTab,
    setActiveTab,
    setHubPageMounted,
    setActiveEmailLoad,
    setActiveBrokerInquiry,
    clearActiveBrokerInquiry,
    removeTab,
    setTabStatus,
    setLastEventId,
    setTabMode,
    setTabSenderEmail,
    setTabModelName,
    startAutonomousRun,
    completeAutonomousRun,
    setAiHubSearchUnlimited,
    setSearchFlowExperimentsV2Enabled,
    setAiHubLoadsUiExperimentsEnabled,
    setDeepagentChatPlanningPromptEnabled,
    setAutonomousMaxRunMs,
    requestPillCollapse,
    requestPillExpand,
    setScoringSummaryFlash,
    setTabCounts,
    setDeletingTab,
    setStreamingText,
    retractStreamedMessage,
    setStreamingThinking,
    setIsSending,
    setIsStreaming,
    setSendFailure,
    setMessages,
    mergeMessages,
    addMessage,
    prependMessages,
    setMessagesPage,
    setLoadingMoreMessages,
    setToolCallStart,
    setToolCallResult,
    setLoads,
    appendLoads,
    setLoadsLoading,
    setTotalLoadsCount,
    decrementTotalLoadsCount,
    setLoadsSearchQuery,
    setStatusFilter,
    setResponseFilter,
    addLoad,
    addLoads,
    upsertLoad,
    updateLoad,
    batchUpdateLoads,
    incrementLoadUnread,
    bumpEmailVersion,
    resetLoadUnread,
    addCallToLoad,
    applyCallResult,
    removeLoads,
    addQueuedInput,
    clearQueuedInputs,
    setSearchBarCollapsed,
    setPendingRequests,
    addPendingRequest,
    resolvePendingRequest,
    setChatQuestionDraft,
    clearChatQuestionDraft,
    updateTabTitle,
    setSearchForm,
    mergeSearchForm,
    markTabSearched,
    setFirstSearchLatency,
    setProcessingSearch,
    setAnalyzingBoard,
    setAnalyzingBoardPhase,
    setAnalyzingBoardProgress,
    setScoringStartedAt,
    setAutonomousStartedAt,
    setAutonomousExpired,
    resumeAutonomousRun,
    setScoringProgress,
    setAnalyzingAnswers,
    markChatQuestionLive,
    dismissLiveChatQuestion,
    transitionLoadsToRanking,
    addSyncLoads,
    setLoadStreamingState,
    setLoadStreamingError,
    setLoadSuperseded,
    clearLoadStreamingState,
    setThreadStreamingState,
    clearThreadStreamingState,
    finalizeStream,
    setRoutedMiles,
    setTabPhase,
    setSearchAgentParams,
    clearSearchAgentParams,
    setSearchAgentOptions,
    clearSearchAgentOptions,
    setTabUiMode,
} = agenticHubSlice.actions

// The browser-tab-title counts (all tabs' open questions + unread emails) live in ./selectors —
// selectAllTabsQuestionCount / selectAllTabsUnreadCount, reusing the EmailSelector's aggregates.

export default agenticHubSlice.reducer
